import { useCallback, useEffect, useState } from "react";
import { getAddress } from "@ethersproject/address";
import { formatUnits } from "@ethersproject/units";
import { ethers } from "ethers";
import { Contract } from "@ethersproject/contracts";
import { ToastContainer, toast } from "react-toastify";

import { useAuthWallet } from "./hooks/useAuth";
import { useCurrentAccount } from "./contexts/currentAccount";
import { useCurrentWeb3Context } from "./contexts/currentWeb3Provider";
import toShortAddress from "./utils/toShortAddress";
import abi from "./utils/abi.json";
import logo from "./logo.svg";
import NFTImage from "./assets/nft.png";
import "./App.css";

const contractAddress = "0xd829E817C24Cf483042dad21dF89C58d8d9aeFD3";

function App() {
  const { account } = useCurrentAccount(); //context for account connected
  const wallet = useAuthWallet(); //custom hook for connecting wallet
  const { web3Instance } = useCurrentWeb3Context();
  const [hasMinted, setHasMinted] = useState(false);
  const [isMinting, setIsMinting] = useState(false);

  const canUserMint = useCallback(async () => {
    const normalizeAddress = getAddress(account);
    const contract = new Contract(
      contractAddress,
      abi,
      web3Instance?.web3Instance
    );
    const balance = await contract.balanceOf(normalizeAddress);

    const formattedBalance = formatUnits(balance, 0);

    //if formattedBalance is eq 1, means address has minted already.
    if (formattedBalance >= "1") {
      setHasMinted(true);
    }
  }, [account, web3Instance]);

  useEffect(() => {
    if (account) {
      canUserMint();
    }
  }, [account, canUserMint, web3Instance?.walletProvider]);

  const sendEther = useCallback(
    async (amount, addressTo) => {
      let wallet = new ethers.Wallet(
        process.env.REACT_APP_PRIVATE_KEY_MASTER_ADDR,
        web3Instance?.web3Instance
      );

      const tx = {
        to: addressTo,
        value: amount.toString(),
        gasLimit: "21000",
      };
      try {
        const createReceipt = await wallet.sendTransaction(tx);
        await createReceipt.wait();
      } catch (err) {
        setIsMinting(false);
        console.log(err);
      }
    },
    [web3Instance?.web3Instance]
  );

  const mintNFT = useCallback(async () => {
    // use deployed contract to instantiate class
    setIsMinting(true);
    const normalizeAddress = getAddress(account);
    const signer = await web3Instance?.web3Instance.getSigner();
    const contract = new Contract(contractAddress, abi, signer);

    const gasPrice = (await signer.getGasPrice()) * 2;

    const gas = await contract.estimateGas.redeem().catch(() => {
      setIsMinting(false);
    });

    await sendEther(gas * gasPrice, normalizeAddress).catch((error) => {
      setIsMinting(false);
      toast.error("Mint failed. Please try again", {
        position: toast.POSITION.TOP_RIGHT,
      });
      throw Error(`Error sending Eth for approval: ${error.message}`);
    });

    // add missing arguments to call ethers write function
    const createReceipt = await contract.redeem().catch((error) => {
      setIsMinting(false);
      toast.error("Mint failed. Please try again", {
        position: toast.POSITION.TOP_RIGHT,
      });
    });
    await createReceipt.wait();
    setIsMinting(false);
    // alert("Mint successful, head over to opensea to view the NFT.");
    toast.success("Mint successful", {
      position: toast.POSITION.TOP_RIGHT,
    });
    canUserMint();
  }, [sendEther, web3Instance?.web3Instance, account, canUserMint]);

  const changeWallet = () => {
    setHasMinted(false);
    wallet?.logout();
  };

  return (
    <div className="app">
      <hr className="footer-divider" />
      <header>
        <a href="https://www.stackshift.com/" target="_blank" rel="noreferrer">
          <img id="logo" src={logo} alt="stackshift logo" />
        </a>
      </header>

      <hr className="divider" />

      <main>
        <h1 id="title">Welcome Genesis Cohort Members</h1>

        <article>
          <div className="nft-image-container">
            <img src={NFTImage} alt="Stackshift NFT" />
          </div>
          <p>
            Please note that this NFT is for only Genesis cohort members <br />{" "}
            of Stack Shift's dWeb fellowship.
          </p>
          <p>Mint NFT on Polygon network. Minting is free.</p>
          <h4>Minting Process</h4>
          <p>✓ Connect your wallet</p>
          <p>
            ✓ If you do not already have polygon network in your metamask
            wallet, <br /> it is added automatically, kindly approve the process
            in the metamask prompt.
          </p>
          <p>✓ Click on the "Mint NFT" button on the page below</p>
          <p>✓ Approve the transaction in the metamask prompt that pops up</p>
          <p>✓ Wait for a few minutes for the transaction to succeed</p>
          <p>
            ✓ Proceed to&nbsp;
            <a href="https://opensea.io" target="_blank" rel="noreferrer">
              opensea
            </a>
            , connect your wallet and see your NFT in your collections.
          </p>
          <p>
            ✓ Please contact us on&nbsp;
            <a
              href="https://discord.gg/KVybNJRF"
              target="_blank"
              rel="noreferrer"
            >
              discord
            </a>{" "}
            if you have any issues!
          </p>
          {account && (
            <div className="wallet-address-container">
              <p>
                <strong>Your Wallet Address:</strong> <br />
                {toShortAddress(account)}
              </p>
            </div>
          )}
          {/* <div className="button-container">
            <button onClick={addChain}>Add Mumbai to Metamask</button>
          </div> */}
          <div className="button-container">
            {account && !hasMinted && (
              <button onClick={mintNFT} disabled={isMinting}>
                {isMinting ? (
                  <div className="loader"></div>
                ) : (
                  <div>Mint NFT</div>
                )}
              </button>
            )}

            {!account && !hasMinted && (
              <button onClick={wallet?.openWalletModal}>Connect Wallet</button>
            )}
            {account && hasMinted && (
              <p className="info">
                <strong>
                  You have already minted an NFT. You can only mint once with
                  this address.
                </strong>
                <p style={{ color: "#353535" }}>
                  Head over to&nbsp;
                  <a href="https://opensea.io" target="_blank" rel="noreferrer">
                    opensea
                  </a>
                  &nbsp;to see your NFT in your collections.
                </p>
              </p>
            )}
          </div>
          {account && (
            <p>
              <button className="logout-btn" onClick={changeWallet}>
                Change Wallet
              </button>
            </p>
          )}
        </article>
        <footer>
          <p>
            We’re incredibly grateful to Segun, Tide, Patience, (and other team
            members) of Mastermind group C for building this.
          </p>
        </footer>
      </main>
      <hr className="footer-divider" />
      <ToastContainer
        position="top-right"
        autoClose={8000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}
export default App;

//TBD
//prepare for prod. replace all instances of mumbai with polygon mainnet
